import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  GatsbyImage,
  getImage,
  type IGatsbyImageData
} from 'gatsby-plugin-image';
import styled from 'styled-components';

import { Button, Heading } from '@/components/design-system';
import { HeadingFourStyles } from '@/components/design-system/Heading';
import { FullScreenVideo } from '@/components/global/FullScreenVideo';
import { ANIMATIONS, TRACKING_LABELS } from '@/utils';

const Wrapper = styled.div<{ $cols: number }>`
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
  padding: 0 40px;

  // Dont love this. Basically wanted this to behave like grid, but
  // center the elements if we have less cards than grid columns.
  // ie. 3 cards in a 4 column grid.
  //
  // if we don't need that centering behavior we should just revert
  // this back to grid:
  // display: grid;
  // grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  --gap: 40px;
  --cols: ${({ $cols }) => $cols};

  display: flex;
  justify-content: center;
  align-content: stretch;
  flex-wrap: wrap;
  gap: var(--gap);

  & > * {
    width: calc((100% - (var(--gap) * (var(--cols) - 1))) / var(--cols));
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    --cols: ${({ $cols }) => ($cols === 4 ? 2 : 1)};
  }

  & + & {
    margin-top: var(--gap);
  }
`;

interface CardsProps {
  card1Copy?: string;
  card1CtaLabel?: string;
  card1CtaLink?: string;
  card1Image?: IGatsbyImageData;
  card1Title?: string;

  card2Copy?: string;
  card2CtaLabel?: string;
  card2CtaLink?: string;
  card2Image?: IGatsbyImageData;
  card2Title?: string;

  card3Copy?: string;
  card3CtaLabel?: string;
  card3CtaLink?: string;
  card3Image?: IGatsbyImageData;
  card3Title?: string;

  card4Copy?: string;
  card4CtaLabel?: string;
  card4CtaLink?: string;
  card4Image?: IGatsbyImageData;
  card4Title?: string;

  cardSize?: 'Two Up' | 'Three Up' | 'Four Up';
}

function getCols(size: CardsProps['cardSize']) {
  switch (size) {
    case 'Two Up':
      return 2;

    case 'Three Up':
      return 3;

    case 'Four Up':
    default:
      return 4;
  }
}

const ImageWrapper = styled.div`
  margin-bottom: 24px;

  img {
    width: 100%;
  }
`;

const StyledHeading = styled(Heading)<{ 'data-size'?: CardsProps['cardSize'] }>`
  text-align: left;
  margin-bottom: 16px;

  &[data-size='Four Up'] {
    ${HeadingFourStyles}
  }
`;

const P = styled.p`
  /* @todo replace this with 1rem when we update self serve to use the new page wrapper */
  font-size: 16px;
  line-height: ${({ theme }) => theme.lineHeight.body.base};
  font-weight: ${({ theme }) => theme.fontWeight.body};
`;

const StyledButton = styled(Button)`
  width: fit-content;
  margin-top: 16px;
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px 0;
`;

function getYoutubeThumbnailFromEmbedLink(url: string) {
  const _url = new URL(url);
  const videoId = _url.pathname.split('/').at(-1);
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

function Card(props: {
  cardSize?: CardsProps['cardSize'];
  copy?: string;
  ctaLabel?: string;
  ctaLink?: string;
  image?: IGatsbyImageData;
  size: string;
  title: string;
}) {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const isYoutubeEmbed =
    props.ctaLink && props.ctaLink.includes('www.youtube.com/embed');

  const youtubeThumbnailUrl =
    props.ctaLink && isYoutubeEmbed
      ? getYoutubeThumbnailFromEmbedLink(props.ctaLink)
      : null;

  return (
    <CardWrapper>
      <div>
        {props.image ? (
          <ImageWrapper>
            <GatsbyImage
              alt={`figure - ${props.title}`}
              image={getImage(props.image)!}
              sizes={`(min-width: 1010px) ${props.size}, 100vw`}
            />
          </ImageWrapper>
        ) : youtubeThumbnailUrl ? (
          <ImageWrapper>
            <img alt="Video thumbnail" src={youtubeThumbnailUrl} />
          </ImageWrapper>
        ) : null}
        <StyledHeading data-size={props.cardSize} renderAs="h3">
          {props.title}
        </StyledHeading>
        {props.copy ? <P>{props.copy}</P> : null}
      </div>

      {props.ctaLabel && props.ctaLink ? (
        <StyledButton
          link={props.ctaLink}
          text={props.ctaLabel}
          {...(isYoutubeEmbed
            ? { onClick: () => setIsVideoOpen(true) }
            : { link: props.ctaLink })}
        />
      ) : null}

      {isYoutubeEmbed ? (
        <FullScreenVideo
          embed={
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen={true}
              frameBorder="0"
              height="720"
              src={props.ctaLink}
              title="blog hero video"
              width="1280"></iframe>
          }
          isOpen={isVideoOpen}
          path=""
          trackingLabel={TRACKING_LABELS.VideoPlayed}
          onClose={() => setIsVideoOpen(false)}
        />
      ) : null}
    </CardWrapper>
  );
}

export function Cards(props: CardsProps) {
  return (
    <Wrapper
      $cols={getCols(props.cardSize)}
      {...ANIMATIONS.SimpleFadeIn}
      as={motion.div}>
      {props.card1Title ? (
        <Card
          cardSize={props.cardSize}
          copy={props.card1Copy}
          ctaLabel={props.card1CtaLabel}
          ctaLink={props.card1CtaLink}
          image={props.card1Image}
          size={`${1240 / getCols(props.cardSize)}px`}
          title={props.card1Title}
        />
      ) : null}

      {props.card2Title ? (
        <Card
          cardSize={props.cardSize}
          copy={props.card2Copy}
          ctaLabel={props.card2CtaLabel}
          ctaLink={props.card2CtaLink}
          image={props.card2Image}
          size={`${1240 / getCols(props.cardSize)}px`}
          title={props.card2Title}
        />
      ) : null}

      {props.card3Title ? (
        <Card
          cardSize={props.cardSize}
          copy={props.card3Copy}
          ctaLabel={props.card3CtaLabel}
          ctaLink={props.card3CtaLink}
          image={props.card3Image}
          size={`${1240 / getCols(props.cardSize)}px`}
          title={props.card3Title}
        />
      ) : null}

      {props.card4Title ? (
        <Card
          cardSize={props.cardSize}
          copy={props.card4Copy}
          ctaLabel={props.card4CtaLabel}
          ctaLink={props.card4CtaLink}
          image={props.card4Image}
          size={`${1240 / getCols(props.cardSize)}px`}
          title={props.card4Title}
        />
      ) : null}
    </Wrapper>
  );
}
